import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { CommunityShell, C } from "../../components/CommunityShell";

const FACILITIES = [
  {
    icon: "🩺",
    title: "Doctors' offices & clinics",
    tag: "For your patients",
    points: [
      "Patients leave a 10-minute visit with hours of plain-English explanation waiting for them — the diagnosis, the medications, the labs, all translated.",
      "Fewer call-backs asking \u201Cwhat did the doctor mean?\u201D — patients arrive at follow-ups already understanding their condition.",
      "Print-ready handouts for any condition or medication, written at a reading level every patient can use.",
      "Medication pages explain the why behind each prescription — which is what actually drives adherence.",
    ],
  },
  {
    icon: "🏘️",
    title: "Community centers & libraries",
    tag: "For your neighbors",
    points: [
      "A free health-literacy resource you can offer anyone who walks in — no account, no cost, no jargon.",
      "The Resources module connects people to real safety-net help nearby: food, housing, prescription assistance, hotlines.",
      "Cultural Cooking adapts the dishes your community actually eats for diabetes, blood pressure, kidney disease, and more.",
      "Perfect for health fairs, senior programs, and wellness workshops — project it on a screen and explore together.",
    ],
  },
  {
    icon: "🛡️",
    title: "Insurance companies & health plans",
    tag: "For your members",
    points: [
      "Members who understand their condition manage it better — better understanding means fewer avoidable ER visits and readmissions.",
      "Medication literacy drives adherence: members see exactly what each drug does and why skipping it matters.",
      "Labs and procedures explained in advance reduce no-shows and member confusion about their own care plan.",
      "A member-facing wellness benefit you can point to: unlimited, always-on health explanation in plain English.",
    ],
  },
];

const WAYS = [
  { title: "Waiting rooms & front desks", body: "A QR code on the counter or a bookmark handed out at checkout — patients start reading before they even get home." },
  { title: "After-visit summaries", body: "Add the link to your printed discharge or after-visit paperwork so the explanation continues after the appointment ends." },
  { title: "Member portals & newsletters", body: "Health plans can feature The Community as a wellness benefit inside member portals, onboarding emails, and newsletters." },
  { title: "Events & workshops", body: "Health fairs, senior programs, church health ministries — project it on a screen and walk through a condition together." },
];

const ORG_TYPES = [
  "Doctor's office / clinic",
  "Community center / library",
  "Insurance / health plan",
  "Hospital / health system",
  "School / training program",
  "Other",
];

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", program_interest: ORG_TYPES[0], notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputStyle = { background: C.CARD, border: `1px solid ${C.BORDER}`, color: C.INK, fontFamily: C.SANS };
  const valid = form.name.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && form.organization.trim().length >= 2;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) { setErr("Please fill in your name, a valid email, and your organization."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/leads", { audience: "facility", ...form });
      setDone(true);
    } catch {
      setErr("Couldn't send your message — please try again in a moment.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="rounded-2xl border p-8 text-center" style={{ background: C.CARD, borderColor: C.BORDER }} data-testid="facility-contact-success">
        <p className="text-[26px] mb-2" aria-hidden="true">✅</p>
        <h3 className="text-[22px] font-extrabold tracking-tight mb-2" style={{ color: C.INK, fontFamily: C.DISPLAY }}>Message received.</h3>
        <p className="text-[14px] max-w-md mx-auto" style={{ color: C.SUB }}>
          Thanks for reaching out — we'll get back to you at <span className="font-bold" style={{ color: C.INK }}>{form.email}</span> shortly.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-2xl border p-6 sm:p-8" style={{ background: C.CARD, borderColor: C.BORDER }} data-testid="facility-contact-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input value={form.name} onChange={set("name")} placeholder="Your name *"
          data-testid="facility-contact-name"
          className="px-4 py-2.5 rounded-xl text-[14px] focus:outline-none" style={inputStyle} />
        <input type="email" value={form.email} onChange={set("email")} placeholder="Work email *"
          data-testid="facility-contact-email"
          className="px-4 py-2.5 rounded-xl text-[14px] focus:outline-none" style={inputStyle} />
        <input value={form.organization} onChange={set("organization")} placeholder="Organization name *"
          data-testid="facility-contact-org"
          className="px-4 py-2.5 rounded-xl text-[14px] focus:outline-none" style={inputStyle} />
        <select value={form.program_interest} onChange={set("program_interest")}
          data-testid="facility-contact-type"
          className="px-3 py-2.5 rounded-xl text-[13px] focus:outline-none" style={inputStyle}>
          {ORG_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <textarea value={form.notes} onChange={set("notes")} rows={4}
        placeholder="How can we help? Tell us about your patients, members, or community…"
        data-testid="facility-contact-notes"
        className="w-full px-4 py-2.5 rounded-xl text-[14px] focus:outline-none mb-3 resize-y" style={inputStyle} />
      {err && <p className="text-[13px] mb-3" style={{ color: "#A33D2A" }} data-testid="facility-contact-error">{err}</p>}
      <button type="submit" disabled={busy} data-testid="facility-contact-submit"
        className="px-7 py-3 rounded-full text-[14px] font-bold disabled:opacity-40 transition-all hover:-translate-y-0.5"
        style={{ background: C.ACCENT, color: "#FFFDF8", fontFamily: C.SANS }}>
        {busy ? "Sending…" : "Send message →"}
      </button>
    </form>
  );
}

export default function CommunityFacilities() {
  return (
    <CommunityShell
      testid="community-facilities-page"
      eyebrow="Facilities & Partners"
      title="Built for the places that care for people."
      intro="Doctors' offices, community centers, and insurance companies all face the same problem: patients leave without truly understanding their own health. The Community closes that gap — for free, in plain English, on any device."
    >
      {/* Partner cards */}
      <section data-testid="community-facilities-cards" className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-16">
        {FACILITIES.map((f) => (
          <div
            key={f.title}
            data-testid={`community-facility-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="rounded-2xl border p-6 flex flex-col"
            style={{ background: C.CARD, borderColor: C.BORDER }}
          >
            <div className="flex items-center gap-3 mb-1">
              <span className="text-[26px] leading-none" aria-hidden="true">{f.icon}</span>
              <span className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: C.ACCENT }}>{f.tag}</span>
            </div>
            <h3 className="text-[19px] font-bold tracking-tight mb-4" style={{ color: C.INK, fontFamily: C.DISPLAY }}>{f.title}</h3>
            <ul className="space-y-3">
              {f.points.map((p, i) => (
                <li key={i} className="flex gap-2.5 text-[13.5px] leading-relaxed" style={{ color: C.SUB }}>
                  <span className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0" style={{ background: C.SAGE }} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      {/* How partners use it */}
      <section data-testid="community-facilities-ways" className="mb-16">
        <p className="text-[12px] font-bold uppercase tracking-[0.26em] mb-3" style={{ color: C.ACCENT }}>How partners use it</p>
        <h2 className="text-[26px] sm:text-[32px] font-extrabold tracking-tight mb-6" style={{ color: C.INK, fontFamily: C.DISPLAY }}>
          Four ways to put it in front of your people.
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {WAYS.map((w) => (
            <div
              key={w.title}
              data-testid={`community-facilities-way-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="rounded-2xl border p-6"
              style={{ background: C.CARD, borderColor: C.BORDER }}
            >
              <h3 className="text-[17px] font-bold tracking-tight mb-2" style={{ color: C.INK, fontFamily: C.DISPLAY }}>{w.title}</h3>
              <p className="text-[14px] leading-relaxed" style={{ color: C.SUB }}>{w.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section data-testid="community-facilities-contact" className="mb-16">
        <p className="text-[12px] font-bold uppercase tracking-[0.26em] mb-3" style={{ color: C.ACCENT }}>Get in touch</p>
        <h2 className="text-[26px] sm:text-[32px] font-extrabold tracking-tight mb-3" style={{ color: C.INK, fontFamily: C.DISPLAY }}>
          Bring The Community to your patients.
        </h2>
        <p className="text-[15px] leading-relaxed max-w-2xl mb-8" style={{ color: C.SUB }}>
          Tell us about your clinic, center, or plan and we'll reach out with
          everything you need — QR materials, cohort reporting, and a custom
          facility analytics setup.
        </p>
        <ContactForm />
      </section>

      {/* CTA */}
      <section
        data-testid="community-facilities-cta"
        className="rounded-2xl border p-8 sm:p-10 text-center"
        style={{ background: "linear-gradient(165deg, #F6EBD9 0%, #FAF5EC 100%)", borderColor: C.BORDER }}
      >
        <h2 className="text-[24px] sm:text-[30px] font-extrabold tracking-tight mb-3" style={{ color: C.INK, fontFamily: C.DISPLAY }}>
          See the reach for yourself.
        </h2>
        <p className="text-[15px] mb-6 max-w-xl mx-auto" style={{ color: C.SUB }}>
          Every condition, medication, and lab in the library — live coverage
          numbers your board or leadership can look at today.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/community/analytics"
            data-testid="community-facilities-cta-analytics"
            className="px-6 py-3 rounded-full text-[14px] font-bold transition-all hover:-translate-y-0.5"
            style={{ background: C.ACCENT, color: "#FFFDF8", fontFamily: C.SANS }}
          >
            View live analytics →
          </Link>
          <Link
            to="/community/how-it-works"
            data-testid="community-facilities-cta-hiw"
            className="px-6 py-3 rounded-full text-[14px] font-bold transition-all hover:-translate-y-0.5"
            style={{ background: C.CARD, color: C.DEEP, border: `1px solid ${C.BORDER}`, fontFamily: C.SANS }}
          >
            How it works
          </Link>
        </div>
      </section>
    </CommunityShell>
  );
}
